/**
 * Configurações > Conta > Calendário no celular (ADR-0092 e ADR-0093).
 *
 * Em cima, a sincronização de duas vias (Google Agenda e iPhone/Mac por
 * CalDAV, em `SincronizacaoCalendarios`). Embaixo, o link de assinatura (.ics)
 * para quem só quer ver, em qualquer calendário. Só leitura: mudar lá não muda aqui.
 * O link aparece uma vez, ao gerar (o servidor guarda só um resumo dele);
 * gerar de novo troca o link e o antigo para na hora.
 */
import React, { useCallback, useEffect, useState } from "react";

import AsyncButton from "../../../components/AsyncButton";
import BotaoCopiar from "../../../components/BotaoCopiar";
import ConfirmDialog from "../../../components/ConfirmDialog";
import { useAuth } from "../../../context/AuthContext";
import { desligarAssinatura, gerarAssinatura, lerAssinatura, lerInicio, salvarFiltrosDaAssinatura } from "../../../services/agenda";
import SincronizacaoCalendarios from "./SincronizacaoCalendarios";
import AtalhoDoGuia from "../conexoes/AtalhoDoGuia";
import { PASSOS_POR_CALENDARIO } from "../conexoes/passos";
import "./IntegracoesIA.css";

const formato = new Intl.DateTimeFormat("pt-BR", {
  timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
});
const instante = (iso) => (iso ? formato.format(new Date(iso)) : "");

function Secao({ titulo, descricao, children }) {
  return (
    <section className="set-section">
      <div className="set-section__head">
        <h2 className="set-section__title">{titulo}</h2>
        {descricao ? <p className="set-section__desc">{descricao}</p> : null}
      </div>
      <div className="set-section__body">{children}</div>
    </section>
  );
}

function ComoAssinar() {
  const [aba, setAba] = useState(PASSOS_POR_CALENDARIO[0].id);
  const atual = PASSOS_POR_CALENDARIO.find((p) => p.id === aba);
  return (
    <>
      <div className="ia-abas" role="tablist" aria-label="Como assinar em cada calendário">
        {PASSOS_POR_CALENDARIO.map((p) => (
          <button key={p.id} type="button" role="tab" aria-selected={p.id === aba} className={p.id === aba ? "ativa" : ""} onClick={() => setAba(p.id)}>
            {p.nome}
          </button>
        ))}
      </div>
      <ol key={aba} className="ia-passos" role="tabpanel">
        {atual.passos.map((passo) => <li key={passo}>{passo}</li>)}
      </ol>
    </>
  );
}

export default function CalendarioCelularTab() {
  const { request } = useAuth();
  const [estado, setEstado] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [liberada, setLiberada] = useState(true);
  const [link, setLink] = useState("");
  const [erro, setErro] = useState("");
  const [confirmar, setConfirmar] = useState(null);

  const carregar = useCallback(async () => {
    try {
      setEstado(await lerAssinatura(request));
      const inicio = await lerInicio(request).catch(() => null);
      setCategorias(inicio?.categorias || []);
      setErro("");
    } catch (e) {
      if (e?.status === 403) setLiberada(false);
      else setErro(e?.message || "Não deu para carregar o calendário.");
      setEstado({ ativa: false });
    }
  }, [request]);
  useEffect(() => { carregar(); }, [carregar]);

  const gerar = async () => {
    const resposta = await gerarAssinatura(request);
    setLink(resposta.url);
    setEstado(resposta);
  };
  const filtrar = async (mudanca) => {
    setEstado((e) => ({ ...e, ...mudanca }));
    const resposta = await salvarFiltrosDaAssinatura(request, mudanca).catch(() => null);
    if (resposta) setEstado(resposta);
  };
  const alternarCategoria = (id) => {
    const atuais = estado.categorias || [];
    filtrar({ categorias: atuais.includes(id) ? atuais.filter((c) => c !== id) : [...atuais, id] });
  };

  if (!estado) return <Secao titulo="Calendário no celular"><div aria-busy="true" className="ia-carregando">Carregando…</div></Secao>;
  if (!liberada) {
    return (
      <Secao titulo="Calendário no celular">
        <p className="ia-aviso">A agenda nova ainda não está liberada para você, então não há o que assinar por enquanto.</p>
      </Secao>
    );
  }

  return (
    <div className="ia-tela">
      <AtalhoDoGuia texto="Primeira vez? O guia mostra cada passo para iPhone, Android, Google e Outlook." />
      <SincronizacaoCalendarios request={request} />

      <Secao
        titulo="Link para só ver (.ics)"
        descricao="Para qualquer calendário, inclusive o Outlook. É só leitura: o que mudar lá não muda aqui."
      >
        {erro ? <p className="ia-erro">{erro}</p> : null}
        {link ? (
          <div className="ia-revelada" role="status">
            <b>Copie o link agora: ele não aparece de novo.</b>
            <div className="ia-endereco">
              <code title={link}>{link}</code>
              <BotaoCopiar texto={link} rotulo="Copiar link" aria-label="Copiar o link do calendário" />
            </div>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setLink("")}>Já copiei</button>
          </div>
        ) : null}
        {estado.ativa ? (
          <ul className="ia-lista">
            <li className="ia-item">
              <div className="ia-item-t">
                <b>Link ativo</b>
                <small>
                  <code>{`…/${estado.prefixo}…`}</code>
                  {` · criado em ${instante(estado.criada_em)} · ${estado.ultimo_acesso_em ? `último acesso ${instante(estado.ultimo_acesso_em)}` : "ainda não foi lido por nenhum calendário"}`}
                </small>
              </div>
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => setConfirmar("gerar")}>Gerar outro</button>
              <button type="button" className="btn btn--ghost btn--sm ia-perigo" onClick={() => setConfirmar("desligar")}>Desligar</button>
            </li>
          </ul>
        ) : (
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <AsyncButton variant="primary" onClick={gerar} silentSuccess>Gerar link do calendário</AsyncButton>
          </div>
        )}
      </Secao>

      {estado.ativa ? (
        <Secao titulo="O que entra no link" descricao="Tarefas suas e as compartilhadas com você, de 60 dias atrás a 1 ano à frente. Tarefa sem dia fica de fora.">
          <div className="ia-filtros">
            <fieldset className="ia-campo ia-checks">
              <legend>Categorias (nenhuma marcada = todas)</legend>
              {categorias.map((c) => (
                <label key={c.id}>
                  <input type="checkbox" checked={(estado.categorias || []).includes(c.id)} onChange={() => alternarCategoria(c.id)} />
                  {c.nome}
                </label>
              ))}
              {!categorias.length ? <span className="ia-vazio">Você ainda não tem categorias.</span> : null}
            </fieldset>
            <label className="ia-opcao">
              <input type="checkbox" checked={Boolean(estado.incluir_concluidas)} onChange={(e) => filtrar({ incluir_concluidas: e.target.checked })} />
              Mostrar também as concluídas (com ✓ no título)
            </label>
          </div>
        </Secao>
      ) : null}

      <Secao titulo="Como assinar o link">
        <ComoAssinar />
      </Secao>

      <ConfirmDialog
        open={Boolean(confirmar)}
        titulo={confirmar === "gerar" ? "Gerar outro link?" : "Desligar o calendário?"}
        descricao={confirmar === "gerar"
          ? "O link atual para de funcionar na hora. Você vai precisar assinar de novo com o link novo."
          : "Os calendários que assinaram este link param de receber a agenda."}
        confirmarLabel={confirmar === "gerar" ? "Gerar outro" : "Desligar"}
        tone="danger"
        onConfirmar={async () => {
          if (confirmar === "gerar") await gerar();
          else { await desligarAssinatura(request); setEstado({ ativa: false }); setLink(""); }
          setConfirmar(null);
        }}
        onCancelar={() => setConfirmar(null)}
      />
    </div>
  );
}
